import { z } from 'zod';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * T-0161 — delete and restore an engagement, the deleted-engagements list,
 * and the signed-in user's flags (`GET /api/v1/me`).
 *
 * Deleting is a soft delete for firm admins: the engagement is hidden from
 * every list and every page for everyone, its documents and history are
 * kept, and its search index is removed. A firm admin restores it from
 * Firm settings → Deleted engagements; restoring rebuilds the index.
 *
 * The server decides who may do this. Screens read `can_delete_engagements`
 * from `/me` to show or hide the entry points, and never re-implement the
 * rule; the write routes refuse non-admins on their own.
 */

// ===========================================================================
// GET /api/v1/me
// ===========================================================================

export const MeSchema = z
  .object({
    user_id: z.string(),
    email: z.string(),
    name: z.string(),
    role: z.string(),
    can_delete_engagements: z.boolean(),
    /** Firm admins and template curators change discovery frameworks (C2-74). */
    can_curate_frameworks: z.boolean().optional(),
  })
  .passthrough();
export type Me = z.infer<typeof MeSchema>;

export function getMe(): Promise<Me> {
  return apiClient('/api/v1/me', MeSchema);
}

export const meQueryKey = ['me'] as const;

export function useMeQuery() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: getMe,
    // A role changes rarely; one fetch per session is plenty.
    staleTime: 5 * 60_000,
  });
}

/** True only once the server has said so; unknown (loading, error) is false. */
export function useCanDeleteEngagements(): boolean {
  return useMeQuery().data?.can_delete_engagements === true;
}

/**
 * Whether the signed-in user may change discovery frameworks in Firm
 * settings (firm admins and template curators). Discovery shows them the
 * way to Firm settings > Discovery frameworks when the pinned version
 * defines no processes to map (C2-74); everyone else reads plain text.
 */
export function useCanCurateFrameworks(): boolean {
  return useMeQuery().data?.can_curate_frameworks === true;
}

// ===========================================================================
// POST /api/v1/engagements/{engagement_id}:delete
// ===========================================================================

export const DeletedEngagementResultSchema = z
  .object({
    engagement_id: z.string(),
    name: z.string(),
    client_id: z.string(),
    deleted_at: z.string(),
    deleted_by: z.string(),
    already_deleted: z.boolean(),
  })
  .passthrough();
export type DeletedEngagementResult = z.infer<typeof DeletedEngagementResultSchema>;

function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function deleteEngagement(
  engagementId: string,
  confirmName: string,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<DeletedEngagementResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}:delete`,
    DeletedEngagementResultSchema,
    { method: 'POST', body: { confirm_name: confirmName }, idempotencyKey },
  );
}

/** Every list an engagement appears in, plus the deleted list. */
function invalidateEngagementLists(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['engagements', 'list'] });
  void qc.invalidateQueries({ queryKey: ['portfolio'] });
  void qc.invalidateQueries({ queryKey: ['clients'] });
  void qc.invalidateQueries({ queryKey: ['notifications'] });
  void qc.invalidateQueries({ queryKey: deletedEngagementsQueryKey });
}

/**
 * Drop every cached query about one engagement (detail, activity poll,
 * documents, …). Call it after leaving the engagement's pages, so nothing
 * mounted refetches into a 404.
 */
export function forgetEngagementQueries(qc: QueryClient, engagementId: string): void {
  qc.removeQueries({ predicate: (q) => q.queryKey.includes(engagementId) });
}

type CachedList = { engagements?: unknown; top_risks?: unknown } | undefined;

function withoutEngagement(list: unknown, engagementId: string, key: 'id' | 'engagement_id') {
  return Array.isArray(list)
    ? list.filter((row) => (row as Record<string, unknown> | null)?.[key] !== engagementId)
    : list;
}

/**
 * Take a deleted engagement out of the cached portfolio and engagement lists
 * at once, before their refetch lands, so the portfolio and the sidebar
 * never render it again (nor fire its per-row requests, which would 404).
 */
export function dropEngagementFromCachedLists(qc: QueryClient, engagementId: string): void {
  qc.setQueriesData<CachedList>({ queryKey: ['portfolio', 'composite'] }, (old) =>
    old
      ? {
          ...old,
          engagements: withoutEngagement(old.engagements, engagementId, 'id'),
          top_risks: withoutEngagement(old.top_risks, engagementId, 'engagement_id'),
        }
      : old,
  );
  qc.setQueriesData<CachedList>({ queryKey: ['engagements', 'list'] }, (old) =>
    old ? { ...old, engagements: withoutEngagement(old.engagements, engagementId, 'id') } : old,
  );
}

export function useDeleteEngagementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      engagementId,
      confirmName,
      idempotencyKey,
    }: {
      engagementId: string;
      confirmName: string;
      idempotencyKey: string;
    }) => deleteEngagement(engagementId, confirmName, idempotencyKey),
    onSuccess: (_result, { engagementId }) => {
      dropEngagementFromCachedLists(qc, engagementId);
      invalidateEngagementLists(qc);
    },
  });
}

// ===========================================================================
// GET /api/v1/admin/engagements/deleted
// ===========================================================================

export const DeletedEngagementSchema = z
  .object({
    engagement_id: z.string(),
    project_id: z.string(),
    name: z.string(),
    client_id: z.string(),
    client_name: z.string(),
    status: z.string(),
    deleted_at: z.string(),
    deleted_by: z.object({ user_id: z.string(), name: z.string(), email: z.string() }).nullable(),
  })
  .passthrough();
export type DeletedEngagement = z.infer<typeof DeletedEngagementSchema>;

export const DeletedEngagementsResponseSchema = z.object({
  engagements: z.array(DeletedEngagementSchema),
});

export function listDeletedEngagements(): Promise<{ engagements: DeletedEngagement[] }> {
  return apiClient('/api/v1/admin/engagements/deleted', DeletedEngagementsResponseSchema);
}

export const deletedEngagementsQueryKey = ['admin', 'engagements', 'deleted'] as const;

export function useDeletedEngagementsQuery(opts: { enabled: boolean }) {
  return useQuery({
    queryKey: deletedEngagementsQueryKey,
    queryFn: listDeletedEngagements,
    enabled: opts.enabled,
  });
}

// ===========================================================================
// POST /api/v1/engagements/{engagement_id}:restore
// ===========================================================================

export const RestoredEngagementSchema = z
  .object({
    engagement_id: z.string(),
    name: z.string(),
    client_id: z.string(),
    status: z.string(),
    restored: z.boolean(),
    reindex: z.object({ documents_enqueued: z.number(), documents_skipped: z.number() }).nullable(),
  })
  .passthrough();
export type RestoredEngagement = z.infer<typeof RestoredEngagementSchema>;

export function restoreEngagement(
  engagementId: string,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<RestoredEngagement> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}:restore`,
    RestoredEngagementSchema,
    { method: 'POST', idempotencyKey },
  );
}

export function useRestoreEngagementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      engagementId,
      idempotencyKey,
    }: {
      engagementId: string;
      idempotencyKey: string;
    }) => restoreEngagement(engagementId, idempotencyKey),
    onSuccess: (_result, { engagementId }) => {
      // Anything cached from before the delete is stale.
      forgetEngagementQueries(qc, engagementId);
      invalidateEngagementLists(qc);
    },
  });
}

export { newIdempotencyKey as newLifecycleIdempotencyKey };
